import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Card, Row, Col, Button, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import moment from 'moment';
import { FaPlus, FaTrash } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import Tabela from '~/Components/TabelaDinamica/Tabela';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import * as api from '~/Services/Service';
import DesktopOnlyGuard from './components/DesktopOnlyGuard';
import ModalCadastro from './components/ModalCadastro';

// W-0539 — Contas Prefeitura Sem Convênio
// Migração de uContaPrefeituraSemConvenio.pas (Delphi):
//   :104-185 cdContaSemConvenio.Open (listagem),
//   :144-167 confirmação de exclusão,
//   :184-220 fluxo de salvar.
// ADRs locais: 001 (RBAC), 002 (POST /remover), 003 (sem paginação), 004 (desktop-only).
//
// LICOES aplicadas:
//   §1.5 key estável — `${codigo_grupo_empresa}-${codigo_conta}` injetado em `id`
//   §1.7 sorting default GE ASC + Conta ASC
//   ciclo 9 L4 erro API — interceptor já fez unwrap, ler err.message

const extrairMensagemErro = (err, fallback) => {
  if (err && err.message) return err.message;
  if (typeof err === 'string') return err;
  return fallback;
};

const compararStrings = (a, b) => {
  const sa = (a || '').toString().toLowerCase();
  const sb = (b || '').toString().toLowerCase();
  if (sa < sb) return -1;
  if (sa > sb) return 1;
  return 0;
};

const ordenarVinculos = (lista) =>
  [...lista].sort((a, b) => {
    const cmpGe = compararStrings(a.apelido_grupo_empresa, b.apelido_grupo_empresa);
    if (cmpGe !== 0) return cmpGe;
    return (a.codigo_conta || 0) - (b.codigo_conta || 0);
  });

const ContasPrefeituraSemConvenio = () => {
  const [vinculos, setVinculos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroGe, setFiltroGe] = useState(null);
  const [filtroConta, setFiltroConta] = useState(null);
  const [modalCadastroAberto, setModalCadastroAberto] = useState(false);
  const [confirmRemover, setConfirmRemover] = useState({
    show: false,
    item: null,
  });

  const carregarVinculos = useCallback(() => {
    setLoading(true);
    api
      .listarContasPrefeituraSemConvenio()
      .then((res) => {
        const lista = Array.isArray(res) ? res : [];
        setVinculos(ordenarVinculos(lista));
      })
      .catch((err) => {
        const msg = extrairMensagemErro(err, 'Erro ao carregar vínculos.');
        toast.error(msg);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    carregarVinculos();
  }, [carregarVinculos]);

  // ADR-003 — filtros client-side: derivam opções da lista carregada e aplicam .filter() no array em memória.
  const opcoesFiltroGe = useMemo(() => {
    const seen = new Map();
    vinculos.forEach((v) => {
      if (!seen.has(v.codigo_grupo_empresa)) {
        seen.set(
          v.codigo_grupo_empresa,
          v.apelido_grupo_empresa || String(v.codigo_grupo_empresa)
        );
      }
    });
    return Array.from(seen.entries())
      .map(([codigo, apelido]) => ({
        value: codigo,
        label: `${codigo} - ${apelido}`,
      }))
      .sort((a, b) => compararStrings(a.label, b.label));
  }, [vinculos]);

  const opcoesFiltroConta = useMemo(() => {
    const seen = new Map();
    vinculos
      .filter((v) => (filtroGe ? v.codigo_grupo_empresa === filtroGe.value : true))
      .forEach((v) => {
        if (!seen.has(v.codigo_conta)) {
          seen.set(v.codigo_conta, v.apelido_conta || String(v.codigo_conta));
        }
      });
    return Array.from(seen.entries())
      .map(([codigo, apelido]) => ({
        value: codigo,
        label: `${codigo} - ${apelido}`,
      }))
      .sort((a, b) => a.value - b.value);
  }, [vinculos, filtroGe]);

  const vinculosFiltrados = useMemo(() => {
    return vinculos
      .filter((v) =>
        filtroGe ? v.codigo_grupo_empresa === filtroGe.value : true
      )
      .filter((v) => (filtroConta ? v.codigo_conta === filtroConta.value : true))
      .map((v) => ({
        ...v,
        // LICOES §1.5 — key estável composta para o `id` consumido por TabelaDinamica.
        id: `${v.codigo_grupo_empresa}-${v.codigo_conta}`,
        ge_display: `${v.codigo_grupo_empresa} - ${v.apelido_grupo_empresa || ''}`,
        conta_display: `${v.codigo_conta} - ${v.apelido_conta || ''}`,
        data_inclusao_display: v.data_inclusao
          ? moment(v.data_inclusao).format('DD/MM/YYYY HH:mm')
          : '-',
      }));
  }, [vinculos, filtroGe, filtroConta]);

  const handleNovo = () => setModalCadastroAberto(true);

  const handleSolicitarRemocao = (item) => {
    setConfirmRemover({ show: true, item });
  };

  const handleConfirmarRemocao = (confirmado) => {
    const item = confirmRemover.item;
    setConfirmRemover({ show: false, item: null });
    if (!confirmado || !item) return;
    api
      .removerContaPrefeituraSemConvenio({
        codigo_grupo_empresa: item.codigo_grupo_empresa,
        codigo_conta: item.codigo_conta,
      })
      .then(() => {
        toast.success('Vínculo removido.');
        carregarVinculos();
      })
      .catch((err) => {
        const msg = extrairMensagemErro(err, 'Erro ao remover vínculo.');
        toast.error(msg);
      });
  };

  const handleSucessoCadastro = () => {
    setModalCadastroAberto(false);
    carregarVinculos();
  };

  const colunas = [
    {
      title: 'Grupo Empresa',
      key: 'ge_display',
      ordenacao: true,
    },
    {
      title: 'Conta',
      key: 'conta_display',
      ordenacao: true,
    },
    {
      title: 'Data Inclusão',
      key: 'data_inclusao_display',
      ordenacao: true,
      width: '180px',
    },
  ];

  const acoes = [
    {
      label: '',
      title: 'Remover vínculo',
      icon: <FaTrash />,
      type: 'danger',
      onClick: handleSolicitarRemocao,
    },
  ];

  const mensagemRemocao = confirmRemover.item
    ? `Deseja excluir a conta ${confirmRemover.item.apelido_grupo_empresa || ''} / ${
        confirmRemover.item.codigo_conta
      }-${confirmRemover.item.apelido_conta || ''}?`
    : '';

  return (
    <DesktopOnlyGuard>
      <Container fluid className="px-3 py-3">
        <Header title="Contas Prefeitura Sem Convênio" />

        <Card className="shadow-sm mb-3">
          <Card.Header style={{ backgroundColor: '#0747a6', color: '#fff' }}>
            <strong>Filtros</strong>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <label htmlFor="filtro-ge">Grupo Empresa</label>
                <Select
                  inputId="filtro-ge"
                  options={opcoesFiltroGe}
                  placeholder="Todos"
                  isClearable
                  value={filtroGe}
                  onChange={(opt) => {
                    setFiltroGe(opt);
                    setFiltroConta(null);
                  }}
                  components={{ DropdownIndicator }}
                  styles={customStyles}
                  noOptionsMessage={() => 'Nenhum vínculo carregado'}
                />
              </Col>
              <Col md={6}>
                <label htmlFor="filtro-conta">Conta</label>
                <Select
                  inputId="filtro-conta"
                  options={opcoesFiltroConta}
                  placeholder="Todas"
                  isClearable
                  value={filtroConta}
                  onChange={(opt) => setFiltroConta(opt)}
                  components={{ DropdownIndicator }}
                  styles={customStyles}
                  noOptionsMessage={() => 'Nenhum vínculo carregado'}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-3">
          <Card.Header
            className="d-flex justify-content-between align-items-center"
            style={{ backgroundColor: '#0747a6', color: '#fff' }}
          >
            <strong>Vínculos cadastrados ({vinculosFiltrados.length})</strong>
            <Button variant="light" size="sm" onClick={handleNovo}>
              <FaPlus className="mr-1" /> Novo vínculo
            </Button>
          </Card.Header>
          <Card.Body>
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
              </div>
            ) : (
              <Tabela
                colunas={colunas}
                dados={vinculosFiltrados}
                action
                acoes={acoes}
                actionColumnWidth="80px"
              />
            )}
          </Card.Body>
        </Card>

        <ModalCadastro
          show={modalCadastroAberto}
          onHide={() => setModalCadastroAberto(false)}
          onSuccess={handleSucessoCadastro}
        />

        <ModalConfirmacao
          show={confirmRemover.show}
          title="Confirmar exclusão"
          message={mensagemRemocao}
          onClose={handleConfirmarRemocao}
        />
      </Container>
    </DesktopOnlyGuard>
  );
};

export default ContasPrefeituraSemConvenio;
